import QRCode from 'qrcode';

// QR 코드 생성 옵션
const QR_OPTIONS = {
  width: 256,
  margin: 2,
  color: {
    dark: '#000000',
    light: '#FFFFFF'
  },
  errorCorrectionLevel: 'M' as const
};

/**
 * QR 코드 데이터 타입
 */
export interface QRCodeData {
  id: string;
  name: string;
  phone: string;
  registration_id: string;
  created_at: string;
}

/**
 * QR 코드 생성 (Base64 Data URL)
 */
export async function generateQRCode(data: QRCodeData): Promise<string> {
  try {
    // QR 코드에 포함될 데이터 (등록번호만 사용)
    const qrData = JSON.stringify({
      registration_id: data.registration_id,
      type: 'exhibition_registration'
    });

    // QR 코드 생성 (Data URL 형태로 반환)
    const qrCodeDataURL = await QRCode.toDataURL(qrData, QR_OPTIONS);
    
    return qrCodeDataURL;
  } catch (error) {
    console.error('QR 코드 생성 실패:', error);
    throw new Error('QR 코드를 생성할 수 없습니다.');
  }
}

/**
 * QR 코드를 Canvas에 그리기
 */
export async function generateQRCodeCanvas(
  data: QRCodeData, 
  canvas: HTMLCanvasElement
): Promise<void> {
  try {
    const qrData = JSON.stringify({
      id: data.id,
      name: data.name,
      phone: data.phone,
      registration_id: data.registration_id,
      created_at: data.created_at,
      type: 'exhibition_registration'
    });

    await QRCode.toCanvas(canvas, qrData, QR_OPTIONS);
  } catch (error) {
    console.error('QR 코드 Canvas 생성 실패:', error);
    throw new Error('QR 코드를 Canvas에 그릴 수 없습니다.');
  }
}

/**
 * QR 코드 데이터 파싱
 */
export function parseQRCodeData(qrString: string): QRCodeData | null {
  try {
    const parsed = JSON.parse(qrString);
    
    // 유효성 검증
    if (
      parsed.type === 'exhibition_registration' &&
      parsed.id &&
      parsed.name &&
      parsed.phone &&
      parsed.registration_id &&
      parsed.created_at
    ) {
      return {
        id: parsed.id,
        name: parsed.name,
        phone: parsed.phone,
        registration_id: parsed.registration_id,
        created_at: parsed.created_at,
      };
    }
    
    return null;
  } catch (error) {
    console.error('QR 코드 데이터 파싱 실패:', error);
    return null;
  }
}

/**
 * QR 코드 ID 생성
 */
export function generateQRCodeId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `QR-${timestamp}-${random}`;
}

/**
 * QR 코드 다운로드 (이미지 파일)
 */
export async function downloadQRCode(
  data: QRCodeData, 
  filename: string = 'exhibition-qr-code.png'
): Promise<void> {
  try {
    const qrCodeDataURL = await generateQRCode(data);
    
    // 다운로드 링크 생성
    const link = document.createElement('a');
    link.href = qrCodeDataURL;
    link.download = filename;
    
    // 임시로 DOM에 추가하고 클릭 후 제거
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('QR 코드 다운로드 실패:', error);
    throw new Error('QR 코드를 다운로드할 수 없습니다.');
  }
}

/**
 * QR 코드 유효성 검증
 */
export function validateQRCode(qrData: QRCodeData): boolean {
  return !!(
    qrData.id &&
    qrData.name &&
    qrData.phone &&
    qrData.registration_id &&
    qrData.created_at
  );
} 